import React from 'react'
import CardTable from '../../../../../components/common/CardTable';
import Loader from '../../../../../components/common/Loader'

/**
 * Reusable wrapper for all empty state displays
 * Does NOT include ScrollToTopBtn to avoid DOM reconciliation issues
 */
export const EmptyStateWrapper = React.memo(({ children }: { children: React.ReactNode }) => (
  <div className="page-container">
    <CardTable>
      <div className="w-full flex items-center justify-center min-h-[400px]">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center text-center">{children}</div>
        </div>
      </div>
    </CardTable>
  </div>
))

EmptyStateWrapper.displayName = 'EmptyStateWrapper'

/**
 * Loading state full page component
 */
export const LoadingState = React.memo(() => (
  <div className="page-container">
    <CardTable>
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader />
      </div>
    </CardTable>
  </div>
))

LoadingState.displayName = 'LoadingState'

/**
 * Access denied state component
 */
export const AccessDeniedState = React.memo(() => (
  <EmptyStateWrapper>
    <h3 className="h3 text-red-600 mb-2">Access denied</h3>
    <p className="text-gray-700 text-sm">
      You do not have permission to view the Manager Portal.
    </p>
  </EmptyStateWrapper>
))

AccessDeniedState.displayName = 'AccessDeniedState'

/**
 * No applications found state component
 */
export const NoApplicationsState = React.memo(() => (
  <EmptyStateWrapper>
    <h3 className="h3 text-gray-600 mb-2">No applications found</h3>
  </EmptyStateWrapper>
))

NoApplicationsState.displayName = 'NoApplicationsState'

/**
 * Loading overlay component
 */
export const LoadingOverlay = React.memo(() => (
  <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center">
    <Loader />
  </div>
))

LoadingOverlay.displayName = 'LoadingOverlay'

/**
 * Approve button component
 * Reusable button for approving applications
 */
export interface ApproveButtonProps {
  row: any
  onClick: (row: any) => void
  disabled?: boolean
}

export const ApproveButton = React.memo(
  ({ row, onClick, disabled = false }: ApproveButtonProps) => (
    <button
      type="button"
      className="button -primary"
      onClick={() => onClick(row)}
      aria-label={`approve-${row.id}`}
      disabled={disabled}
    >
      Approve
    </button>
  )
)

ApproveButton.displayName = 'ApproveButton'

/**
 * Deny button component
 * Reusable button for denying applications
 */
export interface DenyButtonProps {
  row: any
  onClick: (row: any) => void
  disabled?: boolean
}

export const DenyButton = React.memo(
  ({ row, onClick, disabled = false }: DenyButtonProps) => (
    <button
      type="button"
      className="button -primary"
      onClick={() => onClick(row)}
      aria-label={`deny-${row.id}`}
      disabled={disabled}
    >
      Deny
    </button>
  )
)

DenyButton.displayName = 'DenyButton'

/**
 * Note button component
 * Button to open note modal with pencil icon
 */
export interface NoteButtonProps {
  row: any
  onClick: (row: any) => void
}

export const NoteButton = React.memo(({ row, onClick }: NoteButtonProps) => (
  <button
    type="button"
    className="flex items-center gap-2"
    onClick={() => onClick(row)}
    aria-label={`note-${row.id}`}
  >
    <img src={'/assets/images/pencil-outline.svg'} alt="note icon" className="w-4 h-4" />
    <p className="p3 -medium text-[#0074AD]">Add note</p>
  </button>
))

NoteButton.displayName = 'NoteButton'

/**
 * View details button component
 * Shows link to view application details
 */
export const ViewDetailsButton = React.memo(() => (
  <span className="inline-link medium">View details</span>
))

ViewDetailsButton.displayName = 'ViewDetailsButton'